import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import { getEstimateSettings } from 'domains/estimate/Estimate.store';
import { getUserGarageInfo } from 'domains/garage/Garage.store';
import { formatAddress, getFirstValidAddress } from 'domains/garage/Garage.utils';
import { Flex, LightCard, Text } from 'UI';
import { getData, hasData } from 'utils';
import { SDealerGrid } from './DealerSection.styled';

const DealerField = ({ label, value }: { label: string; value?: string }) => {
  if (!value) return null;
  return (
    <Flex direction={'column'}>
      <Text type="base" bold noWrap disableGutter>
        {label}
      </Text>
      <Text type="base" ellipsis disableGutter>
        {value}
      </Text>
    </Flex>
  );
};

const DealerSection = () => {
  const { t } = useTranslation();
  const garageInfo = getData(useSelector(getUserGarageInfo));
  const address = garageInfo ? getFirstValidAddress(garageInfo) : undefined;
  const settings = useSelector(getEstimateSettings);

  const institutionLabel =
    garageInfo?.countryCode === 'FR'
      ? t('estimate.garage_info.siret_number', 'N° Siret')
      : t('estimate.garage_info.institution_number', 'Institution number');

  const vatIntraValue = hasData(settings) && settings.intraCommunityVat ? settings.intraCommunityVat : undefined;

  return (
    <DataContainer
      data={garageInfo}
      ErrorState={() => (
        <LightCard title={''}>
          <Flex align={'center'} justify={'center'} minHeight={100}>
            <ErrorWithLabel
              label={t(
                'estimate.garage_info.backend_error',
                'Garage information temporary unavailable, please try again later.',
              )}
            />
          </Flex>
        </LightCard>
      )}
    >
      <LightCard title={garageInfo?.name ?? t('estimate.garage_info.title', 'Garage information')}>
        <SDealerGrid>
          <DealerField label={t('common.email', 'E-mail address')} value={address?.email} />
          <DealerField label={t('estimate.garage_info.tel', 'Tel')} value={address?.phone} />
          <DealerField
            label={t('estimate.garage_info.postal_address', 'Postal address')}
            value={formatAddress(address)}
          />
          <DealerField label={t('estimate.garage_info.postal_code', 'Postal code')} value={address?.zipCode} />
          <DealerField label={t('estimate.garage_info.city', 'City')} value={address?.city} />
          <DealerField label={institutionLabel} value={garageInfo?.registrationNumber} />
          <DealerField label={t('estimate.garage_info.vat_intra', 'VAT intra')} value={vatIntraValue} />
        </SDealerGrid>
      </LightCard>
    </DataContainer>
  );
};

export default DealerSection;
